<script setup lang="ts">
import { onMounted, computed, ref } from "vue";
import { useAppStore } from "@/ts/stores/app";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { HamburgerButton } from "@/ts/components/elements";

const breakpoints = useBreakpoints(breakpointsTailwind);

const showPageLinks = breakpoints.greater("lg");
const showHamburgerButton = breakpoints.smaller("lg");

const appStore = useAppStore();

interface Props {
    centerLinks?: Array<{ class: string; target: string; title: string; url: string }>;
    rightLinks?: Array<{ class: string; target: string; title: string; url: string }>;
    alternateHover: boolean;
    facebookUrl?: string;
    instagramUrl?: string;
    twitterUrl?: string;
    theme: "black" | "white";
}

const props = defineProps<Props>();
const loaded = ref(false);

onMounted(() => {
    let last_known_scroll_position = 0;
    let ticking = false;
    loaded.value = true;

    window.addEventListener("scroll", function () {
        last_known_scroll_position = window.scrollY;

        if (!ticking) {
            window.requestAnimationFrame(function () {
                if (last_known_scroll_position > 50) {
                    appStore.navigation.header.sticky = true;
                } else {
                    appStore.navigation.header.sticky = false;
                }

                ticking = false;
            });

            ticking = true;
        }
    });
});

function currentlyViewing(url: string): boolean {
    const reg = /.+?:\/\/.+?(\/.+?)(?:#|\?|$)/;
    const results = reg.exec(url);
    if (results && results.length > 0) {
        const pathname = results[1];
        return window.location.href.indexOf(pathname) >= 0;
    }
    return false;
}

const logo = computed(() => {
    const hostnameWithoutPort = window.location.host.split(":")[0];
    let sitename = hostnameWithoutPort.split(".")[0];
    let logoname = sitename;

    if (logoname.includes("plugin")) {
        logoname = "plugin";
    } else if (sitename === "localhost" || "hajee-web" || "hajee") {
        logoname = "hajee-logo-" + (1 + Math.floor(Math.random() * 5));
    }

    return logoname;
});
</script>

<template>
    <transition name="fade" appear>
        <header ref="header" class="h-[4.375rem] md:h-20" :class="[appStore.navigation.header.sticky ? 'sticky' : 'not-sticky', appStore.navigation.slideout.visible ? 'slideout-visible' : '']">
            <div class="container h-full">
                <div class="flex h-full items-center md:justify-center">
                    <transition name="fade">
                        <a href="/" class="mr-auto inline-flex h-full opacity-0 transition-opacity delay-1000 duration-500" :class="{ '!opacity-100': loaded, '-translate-x-14 md:-translate-x-16': logo.indexOf('hajee-logo-') >= 0 }">
                            <svg-vue v-cloak :icon="logo" :class="[appStore.navigation.header.sticky || appStore.navigation.slideout.visible ? 'text-black' : `text-${theme}`, logo !== 'plugin' ? 'h-full' : 'h-10 self-center']" class="w-auto transition-colors"></svg-vue>
                            <span class="sr-only">HAJEE</span>
                        </a>
                    </transition>
                    <transition name="fade">
                        <div v-if="showPageLinks && centerLinks" class="flex space-x-4 lg:space-x-10">
                            <a
                                v-for="page in centerLinks"
                                :href="page.url"
                                :class="[
                                    currentlyViewing(page.url) ? '!text-orange' : '',
                                    page.class === 'link-unstyled' && appStore.navigation.header.sticky ? 'text-black hover:!text-pink' : `text-${theme}`,
                                    page.class === 'link-unstyled' ? `inline-flex items-center text-[.875rem] font-bold uppercase leading-[1.0625rem] transition-colors hover:text-${alternateHover ? 'white' : 'pink'}` : page.class,
                                ]"
                            >
                                {{ page.title }}
                            </a>
                        </div>
                    </transition>
                    <transition name="fade">
                        <div v-if="showPageLinks && rightLinks" class="ml-auto hidden space-x-4 lg:flex lg:space-x-10">
                            <a
                                v-for="page in rightLinks"
                                :href="page.url"
                                :target="page.target && page.target !== '0' ? page.target : ''"
                                :class="[
                                    currentlyViewing(page.url) ? '!text-orange' : '',
                                    page.class === 'link-unstyled' && appStore.navigation.header.sticky ? 'text-black hover:!text-pink' : `text-${theme}`,
                                    page.class === 'link-unstyled' ? `inline-flex items-center text-[.875rem] font-bold uppercase leading-[1.0625rem] transition-colors hover:text-${alternateHover ? 'white' : 'pink'}` : page.class,
                                ]"
                            >
                                {{ page.title }}
                            </a>
                        </div>
                    </transition>
                    <a
                        v-if="instagramUrl && instagramUrl !== ''"
                        :href="instagramUrl"
                        target="_blank"
                        rel="noopener"
                        class="ml-4 hidden transition-colors lg:ml-10 lg:inline"
                        :class="[appStore.navigation.header.sticky ? 'text-black hover:!text-pink' : `text-${theme}`, `hover:text-${alternateHover ? 'white' : 'pink'}`]"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.999 7.377a4.623 4.623 0 1 0 0 9.248a4.623 4.623 0 0 0 0-9.248zm0 7.627a3.004 3.004 0 1 1 0-6.008a3.004 3.004 0 0 1 0 6.008z" fill="currentColor" />
                            <circle cx="16.806" cy="7.207" r="1.078" fill="currentColor" />
                            <path
                                d="M20.533 6.111A4.605 4.605 0 0 0 17.9 3.479a6.606 6.606 0 0 0-2.186-.42c-.963-.042-1.268-.054-3.71-.054s-2.755 0-3.71.054a6.554 6.554 0 0 0-2.184.42a4.6 4.6 0 0 0-2.633 2.632a6.585 6.585 0 0 0-.419 2.186c-.043.962-.056 1.267-.056 3.71c0 2.442 0 2.753.056 3.71c.015.748.156 1.486.419 2.187a4.61 4.61 0 0 0 2.634 2.632a6.584 6.584 0 0 0 2.185.45c.963.042 1.268.055 3.71.055s2.755 0 3.71-.055a6.615 6.615 0 0 0 2.186-.419a4.613 4.613 0 0 0 2.633-2.633c.263-.7.404-1.438.419-2.186c.043-.962.056-1.267.056-3.71s0-2.753-.056-3.71a6.581 6.581 0 0 0-.421-2.217zm-1.218 9.532a5.043 5.043 0 0 1-.311 1.688a2.987 2.987 0 0 1-1.712 1.711a4.985 4.985 0 0 1-1.67.311c-.95.044-1.218.055-3.654.055c-2.438 0-2.687 0-3.655-.055a4.96 4.96 0 0 1-1.669-.311a2.985 2.985 0 0 1-1.719-1.711a5.08 5.08 0 0 1-.311-1.669c-.043-.95-.053-1.218-.053-3.654c0-2.437 0-2.686.053-3.655a5.038 5.038 0 0 1 .311-1.687c.305-.789.93-1.41 1.719-1.712a5.01 5.01 0 0 1 1.669-.311c.951-.043 1.218-.055 3.655-.055s2.687 0 3.654.055a4.96 4.96 0 0 1 1.67.311a2.991 2.991 0 0 1 1.712 1.712a5.08 5.08 0 0 1 .311 1.669c.043.951.054 1.218.054 3.655c0 2.436 0 2.698-.043 3.654h-.011z"
                                fill="currentColor"
                            />
                        </svg>
                    </a>
                    <a
                        v-if="facebookUrl && facebookUrl !== ''"
                        :href="facebookUrl"
                        target="_blank"
                        rel="noopener"
                        class="ml-4 hidden transition-colors lg:inline"
                        :class="[appStore.navigation.header.sticky ? 'text-black hover:!text-pink' : `text-${theme}`, `hover:text-${alternateHover ? 'white' : 'pink'}`]"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" fill="currentColor" />
                        </svg>
                    </a>
                    <a
                        v-if="twitterUrl && twitterUrl !== ''"
                        :href="twitterUrl"
                        target="_blank"
                        rel="noopener"
                        class="ml-4 hidden transition-colors lg:inline"
                        :class="[appStore.navigation.header.sticky ? 'text-black hover:!text-pink' : `text-${theme}`, `hover:text-${alternateHover ? 'white' : 'pink'}`]"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M19.633 7.997c.013.175.013.349.013.523c0 5.325-4.053 11.461-11.46 11.461c-2.282 0-4.402-.661-6.186-1.809c.324.037.636.05.973.05a8.07 8.07 0 0 0 5.001-1.721a4.036 4.036 0 0 1-3.767-2.793c.249.037.499.062.761.062c.361 0 .724-.05 1.061-.137a4.027 4.027 0 0 1-3.23-3.953v-.05c.537.299 1.16.486 1.82.511a4.022 4.022 0 0 1-1.796-3.354c0-.748.199-1.434.548-2.032a11.457 11.457 0 0 0 8.306 4.215c-.062-.3-.1-.611-.1-.923a4.026 4.026 0 0 1 4.028-4.028c1.16 0 2.207.486 2.943 1.272a7.957 7.957 0 0 0 2.556-.973a4.02 4.02 0 0 1-1.771 2.22a8.073 8.073 0 0 0 2.319-.624a8.645 8.645 0 0 1-2.019 2.083z"
                                fill="currentColor"
                            />
                        </svg>
                    </a>
                    <hamburger-button class="ml-auto" :theme="theme" v-if="showHamburgerButton"></hamburger-button>
                </div>
            </div>
        </header>
    </transition>
</template>

<style lang="scss" scoped>
header {
    @apply fixed top-0 z-[998] w-screen transition-all duration-300;

    &.sticky {
        box-shadow: inset 0 -1px 0 0 #d8d8d8;
        @apply bg-white;
    }

    &.slideout-visible {
        box-shadow: none;
        @apply bg-transparent;
    }

    &.white {
        box-shadow: inset 0 -1px 0 0 #d8d8d8;
        @apply bg-white;
    }

    &.transparent {
    }
}
</style>
